import { useEffect, useRef, useState, type KeyboardEvent } from 'react';

/** Open state, focus handling and the keys of the region select: focus lands on
 *  the selected row when it opens, returns to the trigger when it closes, and
 *  Escape closes the tree without reaching a drawer behind it. */
export function useRegionTreeSelect(onChange: (id: string) => void) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const rows = containerRef.current?.querySelectorAll<HTMLElement>('[role="treeitem"]');
    const selected = containerRef.current?.querySelector<HTMLElement>('[aria-selected="true"]');
    (selected ?? rows?.[0])?.focus();
  }, [open]);

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };
  const select = (id: string) => {
    onChange(id);
    close();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' && open) {
      e.stopPropagation();
      close();
    } else if (e.key === 'Tab' && open) {
      setOpen(false);
    } else if (e.key === 'ArrowDown' && !open && e.target === triggerRef.current) {
      e.preventDefault();
      setOpen(true);
    }
  };

  return { open, setOpen, containerRef, triggerRef, select, onKeyDown };
}
